import React, { useState } from 'react';
import { MessageCircle, Send, Mail, Phone, HelpCircle, Bot, User, Sparkles, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
  });
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'bot',
      content: 'Hi! 👋 I\'m an AI assistant trained on Fluxara documentation and help articles. Ask me anything about using Fluxara!',
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Support ticket submitted! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', category: '', subject: '', message: '' });
      setSubmitting(false);
    }, 1500);
  };

  const handleChatSend = () => {
    if (!chatInput.trim()) return;

    // Add user message
    setChatMessages([...chatMessages, { role: 'user', content: chatInput }]);
    
    // Simulate AI response
    setTimeout(() => {
      setChatMessages(prev => [
        ...prev,
        {
          role: 'bot',
          content: 'Thanks for your question! For detailed information, please check our Guides section or submit a support ticket if you need personalized assistance.',
        },
      ]);
    }, 1000);

    setChatInput('');
  };

  const faqs = [
    {
      question: 'How long does design generation take?',
      answer: 'Most designs are generated in 30-60 seconds. Complex designs might take up to 2 minutes.',
    },
    {
      question: 'What if I run out of credits?',
      answer: 'You can upgrade your plan or purchase additional credits anytime from the Pricing page.',
    },
    {
      question: 'Can I get a refund?',
      answer: 'We review refund requests individually. Please contact support@fluxaraai.com with your concerns.',
    },
    {
      question: 'How do I download my designs?',
      answer: 'Click on any project, select your preferred version, and click the Download button for high-res PNG files.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Support - Fluxara</title>
        <meta name="description" content="Get help with Fluxara. Contact support, chat with AI assistant, or browse FAQs." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 gradient-mesh">
        {/* Hero */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500 to-turquoise-500 rounded-3xl mb-6 shadow-2xl">
              <HelpCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              How Can We
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Help You?</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find help resources, submit a support ticket, or chat with our AI assistant
            </p>
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left: Contact Form */}
              <div className="lg:col-span-2">
                <Card className="glass border-0 shadow-2xl">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <Mail className="w-8 h-8 text-cyan-600" />
                      <h2 className="text-3xl font-black text-gray-900">Submit a Support Ticket</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-bold mb-2 text-gray-900">Your Name *</label>
                          <Input
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            className="border-2 border-cyan-200 focus:border-cyan-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2 text-gray-900">Email Address *</label>
                          <Input
                            type="email"
                            placeholder="john@example.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            className="border-2 border-cyan-200 focus:border-cyan-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2 text-gray-900">Category *</label>
                        <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                          <SelectTrigger className="border-2 border-cyan-200">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="technical">Technical Issue</SelectItem>
                            <SelectItem value="billing">Billing Question</SelectItem>
                            <SelectItem value="feature">Feature Request</SelectItem>
                            <SelectItem value="bug">Bug Report</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2 text-gray-900">Subject *</label>
                        <Input
                          placeholder="Brief description of your issue"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          className="border-2 border-cyan-200 focus:border-cyan-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2 text-gray-900">Message *</label>
                        <Textarea
                          placeholder="Please describe your issue in detail..."
                          rows={6}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          className="border-2 border-cyan-200 focus:border-cyan-500 resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700 text-xl py-7 font-bold shadow-xl"
                      >
                        {submitting ? 'Submitting...' : (
                          <>
                            <Send className="mr-3 w-6 h-6" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>

                    <div className="mt-8 pt-8 border-t border-gray-200">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Clock className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Response Time</h3>
                            <p className="text-gray-600 text-sm">We typically respond within 24 hours</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-turquoise-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Mail className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Direct Email</h3>
                            <p className="text-gray-600 text-sm">support@fluxaraai.com</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: AI Chat */}
              <div className="lg:col-span-1">
                <Card className="glass border-0 shadow-2xl h-full flex flex-col">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-cyan-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-turquoise-500 rounded-2xl flex items-center justify-center">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-black text-gray-900">AI Assistant</h2>
                        <p className="text-sm text-gray-600">Get instant help</p>
                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 space-y-4 mb-6 overflow-y-auto max-h-96">
                      {chatMessages.map((msg, index) => (
                        <div key={index} className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.role === 'bot' 
                              ? 'bg-gradient-to-br from-cyan-400 to-turquoise-400' 
                              : 'bg-gray-600'
                          }`}>
                            {msg.role === 'bot' ? <Bot className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                          </div>
                          <div className={`flex-1 p-4 rounded-2xl ${
                            msg.role === 'bot'
                              ? 'bg-gradient-to-br from-cyan-50 to-turquoise-50'
                              : 'bg-gray-100'
                          }`}>
                            <p className="text-gray-800">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Chat Input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ask me anything..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                        className="border-2 border-cyan-200 focus:border-cyan-500"
                      />
                      <Button
                        onClick={handleChatSend}
                        className="bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700"
                      >
                        <Send className="w-5 h-5" />
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 mt-3 text-center">
                      💡 Try: "How do I edit a design?" or "What are credits?"
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-900 mb-4">
                Frequently Asked
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Questions</span>
              </h2>
              <p className="text-xl text-gray-600">Quick answers to common questions</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="glass border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-turquoise-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <HelpCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black mb-4">Other Ways to Reach Us</h2>
              <p className="text-xl text-gray-300">Choose the method that works best for you</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="glass-dark p-8 rounded-2xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Email Support</h3>
                <p className="text-gray-300 mb-4">support@fluxaraai.com</p>
                <p className="text-sm text-gray-400">Response within 24 hours</p>
              </div>

              <div className="glass-dark p-8 rounded-2xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-turquoise-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Live Chat</h3>
                <p className="text-gray-300 mb-4">Available Mon-Fri</p>
                <p className="text-sm text-gray-400">9 AM - 6 PM EST</p>
              </div>

              <div className="glass-dark p-8 rounded-2xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <HelpCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Help Center</h3>
                <p className="text-gray-300 mb-4">Browse guides & tutorials</p>
                <Button
                  variant="outline"
                  className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white"
                  onClick={() => window.location.href = '/guides'}
                >
                  View Guides
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Support;
